export const BUSINESS_PLAN_SECTION = 'EXECUTIVE_SUMMARY__PRODUCT';

export const BusinessPlanSurveySectionGuidanceKeys = [
  BUSINESS_PLAN_SECTION,
  'EXECUTIVE_SUMMARY__CUSTOMERS',
  'EXECUTIVE_SUMMARY__FUTURE_OF_COMPANY',
  'COMPANY_DESCRIPTION__MISSION_STATEMENT',
  'COMPANY_DESCRIPTION__PRINCIPAL_MEMBERS',
  'COMPANY_DESCRIPTION__LEGAL_STRUCTURE',
  'MARKET_RESEARCH__INDUSTRY',
  'MARKET_RESEARCH__DETAILED_CUSTOMERS',
  'MARKET_RESEARCH__COMPANY_ADVANTAGES',
  'MARKET_RESEARCH__REGULATIONS',
  'SERVICE_LINE__PRODUCT_SERVICE',
  'SERVICE_LINE__PRICING_STRUCTURE',
  'SERVICE_LINE__PRODUCT_LIFECYCLE',
  'SERVICE_LINE__INTELLECTUAL_PROPERTY_RIGHTS',
  'SERVICE_LINE__RESEARCH_AND_DEVELOPMENT',
  'MARKETING_AND_SALES__GROWTH_STRATEGY',
  'MARKETING_AND_SALES__COMMUNICATE_WITH_THE_CUSTOMER',
  'MARKETING_AND_SALES__HOW_TO_SELL',
  'FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS',
  'FUNDING_REQUEST__USE_OF_FUNDS',
  'FUNDING_REQUEST__SOURCE_OF_FUNDS',
  'FUNDING_REQUEST__FINANCIAL_IMPACT_AND_BENEFITS',
  'FUNDING_REQUEST__REPAYMENT_PLAN_FOR_LOANS',
] as const;
export type BusinessPlanSurveySectionGuidanceKey =
  (typeof BusinessPlanSurveySectionGuidanceKeys)[number];

type BusinessPlanSurveySectionName = {
  section: string;
  subsection: string;
  wordsMin: number | null;
  wordsMax: number | null;
};
export const sectionsMap: Record<
  BusinessPlanSurveySectionGuidanceKey,
  BusinessPlanSurveySectionName
> = {
  EXECUTIVE_SUMMARY__PRODUCT: {
    section: 'Executive Summary',
    subsection: 'Product',
    wordsMin: 50,
    wordsMax: 80,
  },
  EXECUTIVE_SUMMARY__CUSTOMERS: {
    section: 'Executive Summary',
    subsection: 'Customers',
    wordsMin: 50,
    wordsMax: 80,
  },
  EXECUTIVE_SUMMARY__FUTURE_OF_COMPANY: {
    section: 'Executive Summary',
    subsection: 'Future of Company',
    wordsMin: 50,
    wordsMax: 80,
  },
  COMPANY_DESCRIPTION__MISSION_STATEMENT: {
    section: 'Company Description',
    subsection: 'Mission Statement',
    wordsMin: 15,
    wordsMax: 30,
  },
  COMPANY_DESCRIPTION__PRINCIPAL_MEMBERS: {
    section: 'Company Description',
    subsection: 'Principal Members',
    wordsMin: 15,
    wordsMax: 130,
  },
  COMPANY_DESCRIPTION__LEGAL_STRUCTURE: {
    section: 'Company Description',
    subsection: 'Legal Structure',
    wordsMin: 15,
    wordsMax: 40,
  },
  MARKET_RESEARCH__INDUSTRY: {
    section: 'Market Research',
    subsection: 'Industry',
    wordsMin: 100,
    wordsMax: 150,
  },
  MARKET_RESEARCH__DETAILED_CUSTOMERS: {
    section: 'Market Research',
    subsection: 'Detailed Description of Customers',
    wordsMin: 110,
    wordsMax: 160,
  },
  MARKET_RESEARCH__COMPANY_ADVANTAGES: {
    section: 'Market Research',
    subsection: 'Company Advantages',
    wordsMin: 60,
    wordsMax: 150,
  },
  MARKET_RESEARCH__REGULATIONS: {
    section: 'Market Research',
    subsection: 'Regulations',
    wordsMin: 70,
    wordsMax: 110,
  },
  SERVICE_LINE__PRODUCT_SERVICE: {
    section: 'Service Line',
    subsection: 'Product/Service',
    wordsMin: 70,
    wordsMax: 120,
  },
  SERVICE_LINE__PRICING_STRUCTURE: {
    section: 'Service Line',
    subsection: 'Pricing Structure',
    wordsMin: 50,
    wordsMax: 180,
  },
  SERVICE_LINE__PRODUCT_LIFECYCLE: {
    section: 'Service Line',
    subsection: 'Product Lifecycle',
    wordsMin: 70,
    wordsMax: 120,
  },
  SERVICE_LINE__INTELLECTUAL_PROPERTY_RIGHTS: {
    section: 'Service Line',
    subsection: 'Intellectual Property Rights',
    wordsMin: 70,
    wordsMax: 100,
  },
  SERVICE_LINE__RESEARCH_AND_DEVELOPMENT: {
    section: 'Service Line',
    subsection: 'Research and Development',
    wordsMin: 70,
    wordsMax: 100,
  },
  MARKETING_AND_SALES__GROWTH_STRATEGY: {
    section: 'Marketing & Sales',
    subsection: 'Growth Strategy',
    wordsMin: 50,
    wordsMax: 80,
  },
  MARKETING_AND_SALES__COMMUNICATE_WITH_THE_CUSTOMER: {
    section: 'Marketing & Sales',
    subsection: 'Communicate with the Customer',
    wordsMin: 50,
    wordsMax: 80,
  },
  MARKETING_AND_SALES__HOW_TO_SELL: {
    section: 'Marketing & Sales',
    subsection: 'How to Sell',
    wordsMin: 90,
    wordsMax: 130,
  },
  FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS: {
    section: 'Funding Request',
    subsection: 'Overview of Funding Need',
    wordsMin: 40,
    wordsMax: 70,
  },
  FUNDING_REQUEST__USE_OF_FUNDS: {
    section: 'Funding Request',
    subsection: 'Use of Funds',
    wordsMin: 50,
    wordsMax: 70,
  },
  FUNDING_REQUEST__SOURCE_OF_FUNDS: {
    section: 'Funding Request',
    subsection: 'Sources of Funds',
    wordsMin: 80,
    wordsMax: 110,
  },
  FUNDING_REQUEST__FINANCIAL_IMPACT_AND_BENEFITS: {
    section: 'Funding Request',
    subsection: 'Financial Impact and Benefits',
    wordsMin: 80,
    wordsMax: 120,
  },
  FUNDING_REQUEST__REPAYMENT_PLAN_FOR_LOANS: {
    section: 'Funding Request',
    subsection: 'Repayment Plan (for Loans)',
    wordsMin: 80,
    wordsMax: 120,
  },
};
type BusinessPlanSurveySectionInstruction = {
  publicContent: string;
  hiddenContent?: string;
};
export const instructionsMap: Record<
  BusinessPlanSurveySectionGuidanceKey,
  BusinessPlanSurveySectionInstruction
> = {
  EXECUTIVE_SUMMARY__PRODUCT: {
    publicContent:
      'In this section, briefly describe your main product or service. ' +
      'Focus on what your business offers and how it helps your customers achieve their goals. ' +
      'Tailor the description to highlight the unique aspects of your approach within your industry.',
  },

  EXECUTIVE_SUMMARY__CUSTOMERS: {
    publicContent:
      'In this section, provide a brief overview of your main target audience. ' +
      'This should be a high-level summary of the types of customers who are most likely to benefit from your product or service. ' +
      'Focus on the general characteristics of your ideal customers and their primary needs. ' +
      `\nGuidelines:
  1. Describe the primary audience in a few sentences.
  2. Mention their main goals or pain points that your business addresses.
  3. Keep the description concise and focused, as this is a summary.`,
  },

  EXECUTIVE_SUMMARY__FUTURE_OF_COMPANY: {
    publicContent:
      'This section should provide a snapshot of the strategic direction and goals for the next 3-5 years, ' +
      'giving potential investors and partners a clear sense of the company’s vision and adaptability to market trends.' +
      `\nGuidelines:
  1. Reflect on Industry Trends: Describe how the company will adapt to anticipated changes in your industry. Mention specific trends in real estate, e-commerce, or book publishing that may influence your future direction, and how your business is positioned to take advantage of them.
  2. Highlight Strategic Goals: Outline specific goals such as expanding into new markets, launching new services, or developing additional products that align with your target customers’ evolving needs.
  3. Consider Technology and Innovation: If applicable, mention any plans to incorporate technology or innovative practices, especially if these are expected to enhance your services or streamline operations.
  4. Express Your Long-Term Vision: Define your mission over the long term. This could include expanding service areas, increasing market share, or adapting to a more sustainable or customer-centric model.`,
  },

  COMPANY_DESCRIPTION__MISSION_STATEMENT: {
    publicContent:
      'This section should articulate the core purpose and values of your business in a way that resonates with your target audience and sets you apart from competitors' +
      `\nGuidelines:
  1. Define Your Purpose: Start by clarifying why your business exists. What problem are you solving, or what need are you meeting for your customers?
  2. Focus on Value and Impact: Emphasize the specific value your business provides to its customers. Think about how your services or products help people, solve their problems, or improve their lives.
  3. Make it Memorable and Unique: Your mission should be a reflection of your company's values and what makes it different from others in the industry. Keep it short, but make it meaningful and aligned with your company’s goals and ideals.
  4. Incorporate Your Long-Term Vision: This statement should also hint at the long-term vision of the company and where you see it heading in the future. It can reflect your aspirations and the change you hope to bring to your industry or market.`,
  },

  COMPANY_DESCRIPTION__PRINCIPAL_MEMBERS: {
    publicContent:
      'This section should present the key individuals in your business, highlighting their qualifications, experience, and contributions. This section helps potential investors understand who is leading the company and why they are well-suited for success.' +
      `\nGuidelines:
  1. Introduce Key Team Members: Start by listing the main people involved in your company. This typically includes owners, managers, and other key team members who play a critical role in the business’s operations and growth.
  2. Describe Each Person’s Role: Outline each person’s responsibilities and what they contribute to the company’s success. Be clear about their specific role and how it supports the overall goals of the business.
  3. Highlight Experience and Expertise: For each team member, include relevant industry experience, areas of expertise, or specialized skills. Focus on what makes each person uniquely qualified to lead your company or provide exceptional services.
  4. Showcase Achievements and Background: If applicable, mention past successes, educational backgrounds, certifications, or any achievements that add credibility. For example, if a team member has a track record of business growth or holds relevant certifications, this will boost confidence in your company’s ability.
  5. Include Advisors or Mentors: If you have advisors, mentors, or other professionals who support your business with their expertise, briefly mention them as well. This adds an additional layer of credibility.
  6. Solo Founders: If you’re running the business on your own, focus on your own strengths, experience, and vision for the company. You may want to label this section as "Management Profile" or "Founder's Background" if applicable.`,
    hiddenContent:
      'Prompt the user for the missing member names, roles, experience, achievements and background.',
  },

  COMPANY_DESCRIPTION__LEGAL_STRUCTURE: {
    publicContent:
      'This section should clearly explain your business’s legal setup. This section helps potential investors, partners, and other readers understand the company’s structure, ownership, and any relevant legal considerations.' +
      `\nGuidelines:
  1. Specify Your Legal Structure: Start by stating the legal structure of your business. Common types include sole proprietorship, partnership, Limited Liability Company (LLC), S Corporation, or C Corporation. This choice often affects taxes, liability, and how the business is managed.
  2. Include the State of Registration: If your business is registered in a specific state or country, mention it here, as business laws and tax implications can vary by location.
  3. Explain Why You Chose This Structure: Briefly explain any benefits or reasoning behind your choice. For instance, you might highlight limited liability, tax advantages, or management flexibility.
  4. Ownership Details (if applicable): If your business has multiple owners, co-founders, or silent partners, note their roles and share distribution. Mention if there are any unique aspects of your ownership structure, such as investors, family-owned shares, or advisors.
  5. Mention Future Plans (optional): If there are plans to change the structure in the future (e.g., bringing in investors or transitioning to a different legal structure), you may include a brief note about that.`,
    hiddenContent: 'Do not fabricate the legal structure, prompt the user for the missing details.',
  },

  MARKET_RESEARCH__INDUSTRY: {
    publicContent:
      'This section should clearly describe the market landscape and competitive environment in which your business operates. This section helps readers understand the industry’s current state, potential for growth, and unique position of the business within that landscape.' +
      `\nGuidelines:
  1. Identify Your Industry: Begin by stating the industry your business operates in. This might be as specific as “real estate investment consulting” or as broad as “e-commerce.”
  2. Describe Market Trends: Share relevant trends or recent changes in the industry. For example, is the market expanding or contracting? Are there shifts in consumer behavior, emerging technologies, or regulatory changes affecting your industry?
  3. Outline Competitors: Briefly discuss the types of businesses you’ll be competing with. Are they large corporations, small local businesses, or other startups? You can mention any well-known competitors or typical companies that offer similar services. If your business offers something unique, highlight what sets you apart.
  4. Identify Key Challenges and Opportunities: Discuss any significant challenges your industry is facing, like economic factors or changing customer preferences, and opportunities for growth. This could include technological advancements, increased demand for sustainable practices, or untapped market segments.
  5. Discuss Customer Alternatives (if applicable): If potential customers have alternative ways to meet their needs (e.g., in-house teams, online platforms, or freelance consultants), explain these options. Highlight why your business is a better choice.`,
  },

  MARKET_RESEARCH__DETAILED_CUSTOMERS: {
    publicContent:
      'In this section, go into detail about the specific types of customers your business serves. Include information about demographics, income levels, locations, or other relevant factors. Describe their specific needs, preferences, or challenges, and explain how your products or services are tailored to address these. This section should give a thorough understanding of who your ideal customers are.' +
      `\nGuidelines:
  1. Include detailed demographics such as age range, job titles, income level, or geographical location.
  2. Describe specific needs, interests, or purchasing behaviors of your target audience.
  3. Highlight any unique challenges or goals of this audience and how your business meets those needs.`,
  },

  MARKET_RESEARCH__COMPANY_ADVANTAGES: {
    publicContent:
      'This section should highlight what makes your business unique and compelling. This section is essential for setting the business apart from competitors and demonstrating value to potential investors or clients.' +
      `\nGuidelines:
  1. Highlight Unique Qualities: Begin by identifying the unique qualities or offerings that make your business stand out. These could include specialized products, proprietary methodologies, or innovative technology.
  2. Focus on Value for the Customer: Describe how your advantages directly benefit your customers. For instance, do your offerings provide faster results, better quality, or cost savings?
  3. Leverage Your Team’s Expertise: If applicable, mention the specific skills, experience, or qualifications of your team members that give your business an edge over competitors.
  4. Include Operational Strengths: Operational advantages, such as faster delivery, superior customer support, or streamlined processes, can be compelling to mention. Emphasize anything that makes your service or product delivery more efficient and reliable.
  5. Use Quantifiable Metrics (if possible): Whenever you can, back up your advantages with numbers, such as reduced costs, shorter delivery times, or improved performance percentages. This adds credibility and clarity to your claims.
  6. Tailor to Your Competitors: Think about how your advantages stack up against both large competitors and smaller businesses or individual providers. Highlight what makes your company the better choice in comparison.`,
  },

  MARKET_RESEARCH__REGULATIONS: {
    publicContent:
      'This section should demonstrate your understanding of legal and industry-specific obligations, enhancing credibility with investors and clients.' +
      `\nGuidelines:
  1. List Required Permits and Licenses: Start by identifying any federal, state, or local permits, licenses, or certifications necessary for your business to operate legally. Include specific codes or regulatory titles when relevant.
  2. Industry-Specific Regulations: Describe any specific regulations that apply to your industry. These could include safety standards, environmental regulations, product-specific rules, or customer protection laws. Be as specific as possible.
  3. Compliance Strategy: Explain how you plan to obtain or maintain the necessary credentials and licenses. This could include a brief mention of scheduled renewals, audits, or training programs to stay compliant.
  4. Voluntary Standards (if applicable): If your business follows any voluntary industry standards or best practices, mention these as well. Adhering to high standards beyond the legal minimum can demonstrate your commitment to quality and professionalism.
  5. Highlight Special Considerations: If there are unique regulatory elements, such as exemptions or environmental considerations, be sure to include them. This might include items like environmental impact statements or certifications specific to your industry.`,
  },

  SERVICE_LINE__PRODUCT_SERVICE: {
    publicContent:
      'This section should provide a detailed description of you offerings, showcasing the unique value and benefits your products or services bring to customers. This section is essential for helping potential investors and clients understand exactly what the business provides and why it is valuable.' +
      `\nGuidelines:
  1. Describe Your Offerings: Start by clearly describing the products or services your business offers. Be specific and provide an overview of each item or service, especially if there are multiple components or types of offerings.
  2. Explain the Customer Benefits: Detail how your offerings solve specific customer problems, improve their lives, or help them achieve their goals. For example, mention if your service saves time, reduces costs, or enhances customer convenience.
  3. Highlight Unique Aspects: If there are unique features or innovations in your product or service (such as proprietary technology, eco-friendly processes, or exclusive materials), describe these. Include any intellectual property aspects, such as patents, copyrights, or trademarks, if applicable.
  4. Outline the Product Lifecycle: If relevant, explain the lifecycle of your product, from development to delivery, and mention any plans for future updates, improvements, or research and development.
  5. Add Optional Service Details: If your business offers additional services or customizable options (such as product personalization, after-sales support, or add-on services), list these to show the full range of what you provide.`,
  },

  SERVICE_LINE__PRICING_STRUCTURE: {
    publicContent:
      'This section should describe your pricing models clearly and effectively. The goal is to provide potential clients and investors with transparency about the pricing strategy and its alignment with the business model.' +
      `\nGuidelines:
  1. List Your Pricing Details: Clearly outline the prices of your products or services. Include specific prices for each item or service category, making it easy for readers to understand your pricing model.
  2. Choose a Pricing Strategy:
      - If you sell products, describe whether your pricing is cost-based, value-based, or competitive.
      - If you offer services, mention whether you charge hourly, per project, or based on a subscription model.
  3. Explain the Rationale: Provide a brief explanation of why you’ve chosen your pricing structure. Highlight factors such as market demand, cost of production, competitor pricing, or the perceived value of your offerings.
  4. Highlight Additional Pricing Features:
      - Mention any discounts, packages, or tiered pricing available.
      - Include details of any subscription or membership models, if applicable.
      - If pricing varies based on customer needs (e.g., custom solutions), explain the approach.
  4. Optional Cost Breakdown: For transparency, consider including a cost breakdown that supports your pricing strategy. This could include materials, labor, or overhead costs.
  5. Focus on Value: Make sure to connect your pricing back to the value customers receive. Emphasize how your pricing reflects the quality or uniqueness of your product or service.` +
      `\n\nNotes:
  1. Customization: If your pricing varies based on specific customer needs, mention this to emphasize flexibility.
  2. Transparency: A clear and transparent pricing structure builds trust and helps attract potential clients and investors.
  3. Value: Tie your pricing back to the value your customers receive to justify premium pricing when applicable.
`,
  },

  SERVICE_LINE__PRODUCT_LIFECYCLE: {
    publicContent:
      'This section should describe the main stages of your products or services, from development to delivery, and highlight their readiness for the market.' +
      `\nGuidelines:
  1. Describe the Current Stage: Begin by stating the current stage of your product or service. Is it fully developed and ready for the market, or are you still in the development or prototyping phase?
  2. Explain the Production Process: If applicable, provide a brief overview of how your product is produced or how your service is implemented. Mention key steps, timelines, or dependencies.
  3. Include Market Readiness: If your product or service is ready for the market, note any inventory levels or contracts in place. If not, explain what needs to be completed (e.g., testing, certifications, or approvals).
  4. Discuss Future Plans: Highlight any plans for expanding the product line, enhancing services, or introducing new offerings. This could include scheduled updates, R&D efforts, or scaling production.
  5. Mention Key Milestones (Optional): If your business has achieved significant milestones (e.g., initial product launch, first prototype completion, or securing a major client), include these for added credibility.` +
      `\n\nNotes:
  1. Keep It Simple: Focus on key lifecycle stages—don’t overcomplicate this section.
  2. Highlight Readiness: Emphasize whether your product or service is ready for customers or requires further development.
  3. Forward Thinking: Show that you’re planning for the future, whether it’s scaling, new offerings, or improving processes.`,
  },

  SERVICE_LINE__INTELLECTUAL_PROPERTY_RIGHTS: {
    publicContent:
      'This section should explain the intellectual property (IP) your business owns or plans to acquire and how it protects your unique assets.' +
      `\nGuidelines:
  1. List Current Intellectual Property: Start by listing any existing intellectual property (IP) your business owns, such as trademarks, patents, copyrights, or trade secrets. Include specific details like registration status, locations, or jurisdictions where the IP is protected.
  2. Describe Future IP Plans: If you plan to file for additional IP protection, such as patents for new products, copyrights for creative work, or trademarks for logos or branding, outline these intentions.
  3. Include Domain and Digital Assets: Mention ownership of domain names, social media accounts, or other digital properties that are essential to your brand and business operations.
  4. Explain the Value of Your IP: Highlight how your IP contributes to your business’s competitive advantage, protects your unique offerings, or builds brand recognition.
  5. Mention Licensing or IP Use Agreements (if applicable): If your business licenses IP from others or plans to license your IP to other entities, include a brief explanation.` +
      `\n\nNotes:
  1. Be Specific: Include names, registration statuses, and jurisdictions to demonstrate the scope of your IP protection.
  2. Focus on Business Value: Explain how your IP protects your brand, products, or services and contributes to your competitive advantage.
  3. Include Digital Assets: Highlight ownership of critical digital properties like domain names and social media accounts.`,
  },

  SERVICE_LINE__RESEARCH_AND_DEVELOPMENT: {
    publicContent:
      'This section should explain your ongoing or planned research and development (R&D) activities, showcasing how they will improve your products, services, or processes and help your business stay competitive.' +
      `\nGuidelines:
  1. Outline Current R&D Activities: Describe any ongoing efforts to improve your product, service, or internal processes. Include details about what is being developed or tested.
  2. Identify Future R&D Goals: Share plans for future R&D initiatives, such as creating new products, enhancing existing services, or adopting innovative technologies.
  3. Link to Business Goals: Explain how your R&D activities align with your business objectives, such as entering new markets, addressing customer needs, or staying ahead of competitors.
  4. Highlight the Process: Provide a brief overview of your R&D process, including steps like collecting feedback, conducting experiments, or analyzing market trends.
  5. Discuss Resources and Partnerships: Mention any resources or partnerships that will support your R&D, such as collaboration with industry experts, use of specialized tools, or access to funding.
  6. Include Metrics for Success: If possible, outline measurable goals for your R&D activities, such as timelines for completion, expected outcomes, or key performance indicators (KPIs).` +
      `\n\nNotes:
  1. Be Specific: Detail the goals and methods of your R&D activities to show a clear focus on innovation and improvement.
  2. Link to Competitive Advantage: Highlight how your R&D efforts will help differentiate your business from competitors or address customer pain points.
  3. Include Measurable Outcomes: Add timelines or expected results where possible to demonstrate the feasibility and impact of your R&D initiatives.`,
  },

  MARKETING_AND_SALES__GROWTH_STRATEGY: {
    publicContent:
      'This section should describe how your business plans to grow, expand, and reach new markets, outlining the specific strategies and actions you will take to achieve your growth goals.' +
      `\nGuidelines:
  1. Define Your Goals: Start by outlining the specific growth objectives you aim to achieve, such as increasing market share, expanding into new regions, or launching new products or services.
  2. Describe Your Growth Tactics: Detail the steps you will take to accomplish these goals. Examples might include attending trade shows, running targeted ad campaigns, or establishing partnerships with other businesses.
  3. Include Market Expansion Plans: If applicable, describe how you plan to enter new markets or attract new customer segments. Highlight how you’ll adapt your products, services, or messaging to suit these new markets.
  4. Focus on Scalability: Explain how your business will scale operations to meet the demands of growth, such as hiring additional staff, increasing production capacity, or upgrading technology.
  5. Highlight Innovation: Mention any innovative strategies or tools you’ll use to drive growth, such as leveraging data analytics, adopting new marketing platforms, or introducing cutting-edge products.`,
  },

  MARKETING_AND_SALES__COMMUNICATE_WITH_THE_CUSTOMER: {
    publicContent:
      'This section should describe how your business plans to engage and maintain communication with customers, outlining the methods and tools you will use to build strong relationships and enhance customer loyalty.' +
      `\nGuidelines:
  1. Identify Communication Channels: List the platforms and methods you’ll use to communicate with your customers, such as email, social media, phone, or in-person meetings.
  2. Describe Your Approach: Explain how you will use these channels to interact with customers. Will you focus on regular updates, promotions, educational content, or personalized support?
  3. Include a Feedback Mechanism: Detail how you’ll collect customer feedback to improve your products or services, such as surveys, reviews, or focus groups.
  4. Highlight Personalization: If applicable, mention how you’ll tailor communication to meet individual customer needs, such as personalized emails or targeted social media campaigns.
  5. Focus on Accessibility: Explain how you’ll ensure customers can easily reach you, such as offering multiple points of contact or round-the-clock support.`,
  },

  MARKETING_AND_SALES__HOW_TO_SELL: {
    publicContent:
      'This section should outline the sales process for your business, detailing how you will turn prospects into paying customers and manage sales effectively.' +
      `\nGuidelines:
  1. Describe the Sales Process: Outline the key steps in your sales process, from identifying leads to closing deals. Explain how you will approach prospects, nurture leads, and convert them into customers.
  2. Highlight Staffing and Roles: Mention who is responsible for sales (e.g., owner, dedicated salesperson, or a team). If you plan to hire sales staff or expand your team, include these details as well.
  3. Explain Sales Tools and Methods: List any tools or platforms you will use to support the sales process, such as point-of-sale (POS) systems, customer relationship management (CRM) software, or e-commerce platforms.
  4. Discuss Payment and Delivery: Explain how customers will pay for your products or services (e.g., online, in person, or via subscription) and how they will receive their purchase.
  5. Describe Sales Activities: Include any key activities to generate sales, such as attending events, running promotions, or leveraging digital marketing.
  6. Link to Customer Retention: Briefly mention any follow-up activities or incentives you’ll use to encourage repeat business and referrals.`,
  },

  FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS: {
    publicContent:
      'This section provides a high-level summary of the total funding you’re requesting, the type of funding (e.g., loan, equity), and the overall purpose for which the funding will be used. It gives investors or lenders a quick snapshot of your financial requirements and how they align with your business growth goals. Focus on the "why" and "how much."',
    hiddenContent: 'Do not fabricate the funding needs, prompt the user for the missing details.',
  },
  FUNDING_REQUEST__USE_OF_FUNDS: {
    publicContent:
      'This section offers a detailed breakdown of exactly how the requested funding will be allocated across various business needs. It provides a granular view of "where" the money will go and "how" it will be used to achieve your business objectives. Highlight specific categories and their associated costs.',
  },
  FUNDING_REQUEST__SOURCE_OF_FUNDS: {
    publicContent:
      'This section should outline where your funding will come from, including any personal investments, loans, equity financing, or other sources. Highlight your commitment by mentioning personal contributions or funds already secured, and explain your strategy for obtaining additional financing if required.',
  },
  FUNDING_REQUEST__FINANCIAL_IMPACT_AND_BENEFITS: {
    publicContent:
      'This section should explain how the funding will enhance your business’s financial performance and drive growth. Highlight specific milestones you aim to achieve, such as increased revenue, market expansion, or cost reductions. Show potential investors or lenders the tangible benefits of their contribution, including projected returns, improved operations, or strengthened market position.',
  },
  FUNDING_REQUEST__REPAYMENT_PLAN_FOR_LOANS: {
    publicContent:
      'This section should explain how your business will repay the loan you are requesting. Include the repayment terms, such as the loan amount, interest rate, repayment period, and payment schedule. Highlight how your projected revenue or cash flow will support the repayments. If applicable, mention any contingency plans, such as cash flow buffers or refinancing options, to reassure lenders of your ability to manage debt responsibly.',
  },
};

export type BusinessPlanSurveyExample = {
  header:
    | 'Real Estate Fix & Flip'
    | 'Real Estate Airbnb'
    | 'Real Estate Investors'
    | 'Business Consulting'
    | 'E-Commerce'
    | 'Book Publishing'
    | 'Single Owner LLC'
    | 'Partnership'
    | 'S Corporation'
    | 'Family-Owned Corporation';
  content: string;
};

export const examplesMap: Record<
  BusinessPlanSurveySectionGuidanceKey,
  BusinessPlanSurveyExample[]
> = {
  EXECUTIVE_SUMMARY__PRODUCT: [
    {
      header: 'Real Estate Fix & Flip',
      content: `Greenway Property Solutions specializes in helping real estate investors execute successful Fix & Flip projects. Our team provides property analysis, renovation management, and market assessment services, enabling investors to maximize returns on property sales. By focusing on efficient renovation workflows and targeting high-demand areas, we help clients achieve faster turnaround times and increased profitability on each flip.`,
    },
    {
      header: 'E-Commerce',
      content: `SmartMarket Solutions provides e-commerce business owners with an all-in-one platform for managing inventory, customer orders, and marketing campaigns. Our tools simplify order processing, enhance customer engagement, and support data-driven decision-making, helping online retailers increase sales and customer loyalty. With a user-friendly dashboard and customizable reports, business owners can streamline operations and focus on scaling their online presence.`,
    },
    {
      header: 'Book Publishing',
      content: `PageTurner Publishing specializes in creating and distributing a diverse range of books, including fantasy sports guides, puzzle collections, and self-improvement titles. By utilizing innovative writing techniques and eye-catching designs, we deliver high-quality content that both entertains and educates readers. Our streamlined publishing process and strategic use of platforms like Amazon maximize visibility, helping us reach and engage a wide audience.`,
    },
    {
      header: 'Real Estate Airbnb',
      content: `CityStay Management offers comprehensive property management services for Airbnb hosts. We handle everything from guest communication and booking management to cleaning services and property maintenance, allowing property owners to generate passive income without the stress of daily operations. Our data-driven pricing strategy and dedicated customer support ensure high occupancy rates and positive guest experiences, leading to consistent revenue growth for our clients.`,
    },
  ],
  EXECUTIVE_SUMMARY__CUSTOMERS: [
    {
      header: 'Real Estate Investors',
      content: `Our primary audience consists of real estate investors focused on maximizing property value through strategies such as Fix & Flip, Airbnb rentals, or Wholesale property transactions. These investors seek efficient tools to analyze property potential, streamline property management, and optimize returns on investment. Our platform supports their needs by offering robust financial insights, market trend analysis, and operational resources to increase property profitability and streamline transaction processes.`,
    },

    {
      header: 'E-Commerce',
      content: `Our target customers are owners and managers of small e-commerce businesses aiming to grow their online presence and improve sales conversions. These entrepreneurs often juggle inventory management, customer engagement, and marketing optimization, striving to meet the fast-paced demands of the digital marketplace. Our platform provides essential tools to streamline inventory control, enhance customer relationship management, and implement data-driven marketing strategies, helping e-commerce businesses thrive and scale effectively.`,
    },
    {
      header: 'Book Publishing',
      content: `Our target customers are individuals seeking engaging and diverse content across genres like fantasy sports, puzzles, coloring books, and self-improvement. These readers value high-quality, accessible publications that offer entertainment, relaxation, or personal growth. By providing a wide range of books, we address their desire for both leisure activities and educational enrichment.`,
    },
  ],
  EXECUTIVE_SUMMARY__FUTURE_OF_COMPANY: [
    {
      header: 'Real Estate Fix & Flip',
      content:
        'In the next five years, we plan to increase the volume of properties we acquire, renovate, and resell by leveraging emerging property tech tools for better market analysis and project management. As the demand for modernized, affordable homes continues to rise, we aim to capitalize on this trend by developing a reputation as a trusted provider of quality refurbished homes in urban areas. Our goal is to become a regional leader in the fix & flip market and gradually expand into suburban areas as we build our brand and team.',
    },
    {
      header: 'Book Publishing',
      content:
        "In the next five years, we plan to diversify our book offerings by exploring emerging genres and utilizing AI for content creation. We'll expand our distribution channels beyond Amazon to include other major platforms. By enhancing our marketing strategies and focusing on reader engagement, we aim to increase our market share and establish a strong presence under multiple pen names.",
    },
    {
      header: 'E-Commerce',
      content:
        'As the e-commerce landscape continues to evolve, we aim to expand our product offerings and adopt a multi-channel sales approach by integrating with additional online marketplaces. We plan to enhance our website and mobile app experience by implementing AI-driven recommendations and personalized shopping. By investing in sustainable product sourcing and eco-friendly packaging, we will cater to the growing demand for environmentally responsible products, positioning our brand as a top choice for conscientious consumers. Long-term, we see our brand becoming a household name in niche e-commerce markets, with a loyal customer base that appreciates both quality and sustainability.',
    },
  ],
  COMPANY_DESCRIPTION__MISSION_STATEMENT: [
    {
      header: 'Real Estate Fix & Flip',
      content:
        'Our mission is to transform neglected properties into quality, affordable homes that benefit communities and inspire new beginnings for residents.',
    },
    {
      header: 'E-Commerce',
      content:
        'Our mission is to deliver high-quality, responsibly sourced products that bring convenience and joy to our customers’ daily lives.',
    },

    {
      header: 'Book Publishing',
      content:
        'Our mission is to harness creativity and technology to produce diverse, high-quality books that captivate and inspire readers worldwide.',
    },
  ],
  COMPANY_DESCRIPTION__PRINCIPAL_MEMBERS: [
    //
    {
      header: 'Real Estate Investors',
      content: `Alex Johnson — Founder and CEO. With over 10 years in real estate investment, Alex has a strong background in property acquisition, management, and sales, specializing in Fix & Flip and Airbnb rentals. Alex holds a real estate license and has completed over 100 successful property transactions, demonstrating his expertise in market analysis and value assessment.
Sandra Lee — Operations Manager. Sandra has a background in project management and property maintenance. Her experience in managing renovations and coordinating contractors helps the company maintain high-quality standards and efficiency.
David Chen — Financial Advisor. With a background in investment analysis, David provides guidance on financial strategy, ensuring that the company’s assets are optimized for growth and sustainability.`,
    },
    {
      header: 'E-Commerce',
      content: `Emma Green — Owner and Head of Product. Emma brings five years of experience in e-commerce and product development. She has a knack for identifying market trends and sourcing products that appeal to modern consumers.
Jack Taylor — Marketing Director. Jack has a background in digital marketing and has helped scale e-commerce brands through targeted online advertising and content strategy. His expertise has driven online engagement and conversion rates.
Lily White — Customer Success Manager. With experience in customer service, Lily is committed to ensuring a positive customer experience from the first interaction to post-purchase support. Her focus on customer retention and satisfaction has helped build a loyal customer base.`,
    },
    {
      header: 'Book Publishing',
      content: `Emma Davis — Founder and Chief Publisher. With over 12 years in the publishing industry, Emma has a wealth of experience in content creation and editorial management. She has successfully overseen the publication of numerous bestsellers across various genres, utilizing innovative technologies to enhance reader engagement and market reach.
Michael Thompson — Operations Manager. Michael brings nine years of expertise in operations and project management. His proficiency ensures that the publishing process is streamlined and efficient, from manuscript development to final distribution, while maintaining the highest quality standards.
Sophia Martinez — Marketing Director. With a strong background in digital marketing and audience development, Sophia has a proven track record of boosting book visibility and sales. She employs creative marketing strategies across social media and online platforms to connect with readers and expand our audience base.`,
    },
  ],
  COMPANY_DESCRIPTION__LEGAL_STRUCTURE: [
    {
      header: 'Single Owner LLC',
      content: `EcoTech Solutions is registered as a Limited Liability Company (LLC) in the state of California. We chose this structure to benefit from limited liability protection while retaining management flexibility and favorable tax treatment for a single-owner business.`,
    },
    {
      header: 'Partnership',
      content: `GreenLeaf Real Estate operates as a general partnership, with equal ownership shared between the two founders, Alex White and Jamie Rivera. This structure allows for joint management and decision-making, leveraging each partner’s expertise in real estate development and property management.`,
    },
    {
      header: 'S Corporation',
      content: `BrightStart Educational Resources is incorporated as an S Corporation in Illinois. We selected this structure to offer limited liability protection and pass-through taxation to our shareholders. The company has three equal co-owners, each actively involved in daily operations.`,
    },
  ],
  MARKET_RESEARCH__INDUSTRY: [
    {
      header: 'Real Estate Investors',
      content: `We operate within the real estate investment industry, a sector valued at approximately $3.5 trillion in the U.S. This market includes strategies such as Fix & Flip, short-term Airbnb rentals, and wholesale property investments. Recent data shows a 5% annual growth rate in short-term rentals, driven by rising demand for affordable housing and alternative income streams.
Our main competitors include large real estate investment firms and smaller, independent property investors. Additionally, some investors use online platforms for property sourcing or rely on in-house teams for property management. Key challenges include fluctuating property values and adapting to local regulations on short-term rentals. However, with advancements in property management technology, there’s a projected increase of 15% in revenue for firms that utilize these innovations, presenting significant opportunities to maximize returns in underserved markets.`,
    },

    {
      header: 'E-Commerce',
      content: `We operate within the e-commerce industry, focusing on sustainable products and eco-friendly packaging, part of a global market projected to reach $25 billion by 2025 with a 20% annual growth rate. Consumer surveys show that 65% of buyers prefer brands prioritizing sustainability, making eco-conscious products a rapidly growing segment.
Our competitors include both established e-commerce platforms and boutique stores that offer eco-friendly items. Some customers purchase sustainable goods from large online marketplaces or directly from brands recently adopting eco-friendly practices. Our competitive edge lies in our exclusive focus on sustainability, transparency in sourcing, and commitment to reducing waste throughout our supply chain. While the rising costs of sustainable materials present challenges, this industry is forecasted to grow by 18% annually due to increasing consumer awareness and preference, positioning us to lead in a high-demand market.`,
    },

    {
      header: 'Book Publishing',
      content: `We operate within the book publishing industry, specifically in independent and self-publishing sectors. The industry has experienced significant growth with the rise of digital platforms like Amazon Kindle Direct Publishing, making it easier to reach a global audience. Current market trends show an increasing demand for diverse and niche content, such as fantasy sports guides, puzzle books, and self-improvement titles.
Our competitors include other independent publishers, self-published authors, and traditional publishing houses expanding into digital formats. Key challenges involve market saturation and standing out in a crowded marketplace. However, there are opportunities in leveraging AI for content creation and targeting specialized genres that are underrepresented. As consumers seek unique and engaging content, our adaptive approach positions us to capitalize on these emerging demands.`,
    },
  ],
  MARKET_RESEARCH__DETAILED_CUSTOMERS: [
    {
      header: 'Real Estate Investors',
      content: `Our primary customers are individuals and small investment firms engaged in real estate investment strategies, including Fix & Flip, Airbnb rentals, and Wholesale real estate deals. These customers are typically between 30 and 55 years old, with annual incomes ranging from $80,000 to $500,000. They are based in urban and suburban areas with high real estate turnover rates, where opportunities for property acquisition and resale are frequent.
Our customers are often financially driven and focused on achieving high returns on investment. They value data-driven tools to help analyze property potential, manage renovation projects, and maximize rental income. Additionally, they face challenges such as understanding local housing market trends, calculating accurate ROI, and efficiently managing multiple properties. Our company addresses these needs by providing in-depth property analytics, project management tools, and streamlined compliance resources to facilitate smoother transactions and effective property management.`,
    },
    {
      header: 'E-Commerce',
      content: `Our target audience consists of online shoppers, primarily between the ages of 18 and 45, who are tech-savvy and comfortable making purchases online. These customers tend to be based in densely populated regions with access to reliable shipping services and are looking for a seamless online shopping experience. Many of our customers are attracted to products that offer both quality and value, and they appreciate fast shipping, responsive customer service, and a variety of payment options.
Our customers’ needs often revolve around convenience, personalized shopping experiences, and product quality. They may also seek unique items or specialized products that are not readily available in traditional retail stores. To address these needs, our e-commerce platform offers a user-friendly interface, personalized product recommendations, and multiple shipping options to meet the demands of a fast-paced, digital marketplace. Additionally, we focus on keeping our customers engaged through targeted promotions, loyalty programs, and post-purchase support.`,
    },

    {
      header: 'Book Publishing',
      content: `Our primary customers are avid readers and hobby enthusiasts aged 25 to 55, primarily located in the United States. These individuals have annual incomes ranging from $40,000 to $100,000 and work in diverse professions, including educators, professionals, and creative artists. They seek a variety of engaging content, such as fantasy sports guides, puzzle books, coloring books, and self-improvement titles, to enhance their leisure time and personal growth. 
Our customers value high-quality, well-designed publications that offer both entertainment and educational benefits. Many prefer purchasing books through online platforms like Amazon for convenience and accessibility. They face challenges in finding unique, niche content that aligns with their specific interests. Our publishing services address these needs by providing a wide array of specialized books, utilizing effective online distribution channels, and implementing targeted marketing strategies to ensure our titles reach and resonate with our audience.`,
    },
  ],
  MARKET_RESEARCH__COMPANY_ADVANTAGES: [
    {
      header: 'Real Estate Investors',
      content: `Our firm offers unmatched local expertise, focusing on areas with high growth potential. Unlike larger firms, we are highly selective in property acquisitions, resulting in a 15% higher return on investment for clients compared to the regional average.

Our team consists of licensed real estate professionals with over 20 years of combined experience. We streamline processes with our proprietary market analysis tools, which allow us to identify high-value properties faster, reducing acquisition time by 30% and offering clients quicker, data-driven investment options.`,
    },

    {
      header: 'Book Publishing',
      content: `- Our publishing company distinguishes itself through a diverse range of niche genres, including fantasy sports, puzzles, and self-improvement books, catering to specific reader interests. 
- We utilize advanced AI tools for efficient content creation and optimized keyword strategies on platforms like Amazon, enhancing book visibility and sales. 
- Our team consists of seasoned editors, talented designers, and marketing experts with over 15 years of industry experience, ensuring high-quality publications. 
- Additionally, our streamlined publishing process reduces time-to-market by 25%, allowing us to swiftly adapt to market trends and meet customer demands, setting us apart from both traditional and independent publishers.`,
    },

    {
      header: 'Business Consulting',
      content: `- Customized Solutions: Unlike one-size-fits-all approaches, we tailor our services to each client's specific needs and business goals. This personalized attention has led to a 25% increase in customer satisfaction.
- Rapid Implementation: Our streamlined processes allow us to deliver results 40% faster than the industry average, helping clients see improvements quickly.
- Expertise Across Industries: Our diverse team brings experience from various sectors, offering cross-industry insights that drive innovation and unique solutions for each client.
- 24/7 Client Support: We provide round-the-clock availability, ensuring our clients always have access to expert advice when they need it most, setting us apart from competitors who only offer standard business hours.`,
    },
  ],

  MARKET_RESEARCH__REGULATIONS: [
    {
      header: 'Real Estate Investors',
      content: `Our company must comply with federal, state, and local real estate regulations, including property disclosure requirements, landlord-tenant laws, and fair housing regulations. In particular, we adhere to the Fair Housing Act, 42 U.S.C. § 3601-3619, which prohibits discrimination based on race, color, religion, sex, or national origin.
In addition, each property we manage or renovate must meet building and zoning codes specific to the municipality, and we obtain the necessary permits for structural changes, electrical work, and plumbing. We have also implemented training programs to ensure our staff understand these regulations and maintain compliance. Finally, we voluntarily adhere to ethical guidelines set by the National Association of Realtors, further underscoring our commitment to fair and professional business practices.`,
    },

    {
      header: 'E-Commerce',
      content: `Our business adheres to regulations governing e-commerce, data privacy, and consumer protection. We comply with the California Consumer Privacy Act (CCPA) to protect customer data, as well as the Federal Trade Commission (FTC) regulations on advertising claims, ensuring that our sustainability claims are accurate and transparent.
As an eco-friendly business, we also adhere to the Environmental Protection Agency (EPA) guidelines for sustainable packaging. We regularly audit our supply chain to ensure all materials meet sustainability criteria, including certifications like FSC (Forest Stewardship Council) for paper products. Additionally, we voluntarily comply with the Sustainable Packaging Coalition (SPC) guidelines, demonstrating our commitment to environmentally responsible practices.`,
    },

    {
      header: 'Book Publishing',
      content: `Our publishing company complies with all federal, state, and local regulations essential to the book publishing industry. We obtain necessary licenses such as the ISBN (International Standard Book Number) from Bowker and ensure all publications adhere to copyright laws under Title 17 of the U.S. Code to protect intellectual property rights. Additionally, we follow the regulations set by the Federal Trade Commission (FTC) regarding advertising and marketing claims to maintain transparency and honesty in our promotions.
To maintain compliance, we have established a dedicated compliance team that regularly reviews and updates our practices in line with evolving laws and industry standards. We also participate in voluntary standards set by the Independent Book Publishers Association (IBPA), ensuring high-quality production and ethical business practices. Furthermore, we implement data protection measures in accordance with the California Consumer Privacy Act (CCPA) to safeguard customer information, demonstrating our commitment to both legal obligations and customer trust.`,
    },
  ],

  SERVICE_LINE__PRODUCT_SERVICE: [
    {
      header: 'Real Estate Investors',
      content: `Our business focuses on two key strategies::

  - Fix & Flip: Acquiring undervalued properties, managing renovations, and reselling at a profit. We handle property selection, oversee cost-effective renovations, and market properties for quick, high-value sales.
  - Short-Term Rentals: Transforming properties into high-demand Airbnb rentals by optimizing design, managing bookings, and using dynamic pricing to maximize revenue.

These strategies enable us to generate strong returns while enhancing property value and guest satisfaction.`,
    },

    {
      header: 'E-Commerce',
      content: `We offer a curated line of eco-friendly products, including:

  - Reusable Household Goods: Our line includes items such as stainless steel straws, bamboo toothbrushes, and reusable produce bags.
  - Biodegradable Packaging: We provide biodegradable packaging solutions for small businesses, including compostable mailers and recyclable packing materials.
  - Sustainable Apparel: Our apparel line is made from organic cotton and recycled materials, appealing to environmentally conscious consumers who value style and sustainability.

These products are designed to reduce waste and promote sustainable living. Our unique sourcing practices ensure that every item we sell meets stringent environmental standards, and we are currently developing new eco-friendly product lines to expand our offerings in the near future.`,
    },

    {
      header: 'Book Publishing',
      content: `Our business focuses on several key offerings:
  - Diverse Book Genres: Publishing fantasy sports guides, puzzle books, coloring books, and self-improvement titles to meet varied reader interests.
  - Advanced Content Creation: Leveraging AI and skilled ghostwriters to produce high-quality, engaging content efficiently.
  - Custom Design Services: Offering bespoke cover designs and professional layouts tailored to each book’s genre.
  - Optimized Distribution: Managing Amazon listings with strategic keywords and PPC advertising to enhance visibility and sales.
  - Targeted Marketing: Implementing data-driven campaigns through social media and email marketing to reach and retain our audience.
These offerings ensure we deliver exceptional publications that meet our readers' unique needs and achieve strong market presence.
`,
    },
  ],

  SERVICE_LINE__PRICING_STRUCTURE: [
    {
      header: 'Real Estate Fix & Flip',
      content: `Our Fix and Flip business model is built around strategically acquiring undervalued properties, performing high-quality renovations, and selling them at competitive market prices. Below is an outline of our pricing structure for the key components of this process:

Property Acquisition Costs
  - Average Purchase Price: $150,000 - $300,000 (varies based on location and market conditions).
  - Closing Costs: Approximately 2% - 4% of the purchase price (includes title insurance, attorney fees, and transfer taxes).

Renovation Costs
  - Cosmetic Renovations (e.g., paint, flooring, minor updates): $15,000 - $30,000 per property.
  - Full Renovations (e.g., kitchens, bathrooms, structural work): $40,000 - $100,000 per property.
  - Specialized Work (e.g., HVAC installation, roofing, foundation repairs): Priced individually based on scope (average $5,000 - $15,000).

Selling Costs
  - Realtor Fees: 5% - 6% of the final sale price.
  - Staging and Marketing: $1,500 - $3,000 per property (includes professional photography, online listings, and open houses).
  - Closing Costs: Approximately 1% - 2% of the sale price (includes prorated property taxes and transfer fees).

Profit Margins
  - Target Profit Margin: 15% - 25% of the final sale price after all expenses.
  
Flexible Financing Options
We use a mix of cash purchases and short-term financing options, ensuring flexibility in acquiring properties and managing renovation budgets. We aim to complete each project within 3-6 months, minimizing holding costs and maximizing returns.`,
    },
    {
      header: 'Book Publishing',
      content: `Our pricing strategy for ready-to-publish books on Amazon is designed to maximize both accessibility for readers and profitability for authors:
- Ebooks:
    - Standard Fiction and Non-Fiction Titles: Priced between $2.99 and $9.99 to align with Amazon's 70% royalty tier, optimizing earnings per sale.
    - Short Stories or Novellas: Priced at $0.99 to $2.99 to attract readers seeking shorter content.
- Print Books:
    - Paperback Editions: Prices start at $9.99, with adjustments based on page count and production costs. For example, a 300-page black-and-white paperback incurs a printing cost of approximately $4.45; pricing the book at $15.99 yields a royalty of about $1.95 per copy. 
- Audiobooks:
    - Pricing is determined by Amazon's ACX platform, based on audiobook length, typically ranging from $10 to $30. 
This structured pricing approach ensures competitive positioning in the market while providing fair compensation for authors.`,
    },

    {
      header: 'E-Commerce',
      content: `Our products are priced competitively to align with market demand while reflecting the sustainability and quality of our offerings:

  - Reusable Household Goods:
      --  Stainless Steel Straws (set of 4): $12.99
      --  Bamboo Toothbrushes (set of 3): $15.99
      
  - Biodegradable Packaging:
      --  Compostable Mailers (pack of 50): $25.00
      --  Recyclable Packing Tape (roll): $7.99

We also offer a 10% discount on orders over $100 and a subscription model for monthly shipments, starting at $29.99/month.`,
    },
  ],

  SERVICE_LINE__PRODUCT_LIFECYCLE: [
    {
      header: 'Real Estate Fix & Flip',
      content: `Our business operates on a streamlined project lifecycle for each Fix and Flip property:
  - Acquisition Phase: Properties are sourced and purchased within 30-45 days, ensuring swift entry into the project pipeline.
  - Renovation Phase: Renovations begin immediately after acquisition and are typically completed within 60-90 days, depending on the scope of work.
  - Sales Phase: Properties are listed and sold within 30-60 days post-renovation, leveraging professional staging and targeted marketing.

This efficient lifecycle allows us to turn over each property within a 4-6 month timeline, maximizing returns and minimizing holding costs. Our future plans include scaling operations by increasing the number of concurrent projects and exploring new markets.`,
    },

    {
      header: 'E-Commerce',
      content: `All products in our eco-friendly product line are currently in production and available for purchase.
  - Sourcing Phase: Sustainable materials are procured from certified suppliers, ensuring consistent quality and environmental compliance.
  - Manufacturing Phase: Products are manufactured in small batches to maintain quality control, with an average production cycle of 2-3 weeks.
  - Inventory Phase: We maintain a rolling inventory of at least 200 units per product to meet demand while minimizing waste.

As demand grows, we plan to expand our product line by introducing three new items each quarter, focusing on customer feedback and market trends.`,
    },

    {
      header: 'Book Publishing',
      content: `Our publishing process follows a structured lifecycle to ensure efficiency and quality:
 - Content Creation Phase: Books are written using AI tools, ghostwriters, or in-house authors. This stage includes drafting, editing, and finalizing manuscripts, typically taking 2-6 weeks depending on the book's complexity.
 - Design and Production Phase: Covers are professionally designed, and interior formatting is completed for both print and digital versions. This stage typically takes 1-2 weeks.
 - Publishing and Distribution Phase: Listings are created and optimized on Amazon, including keyword selection and approval. This phase is completed within 1 week.
 - Marketing and Review Phase: PPC campaigns are launched, and strategies for gathering reviews are implemented to boost visibility.
Future plans include diversifying book genres and scaling marketing efforts to increase sales across multiple platforms.`,
    },
  ],

  SERVICE_LINE__INTELLECTUAL_PROPERTY_RIGHTS: [
    {
      header: 'Real Estate Fix & Flip',
      content: `Our business name, "Rapid Flip Investments," is a trademarked entity in the state of Texas. Additionally, we have secured copyrights for our proprietary property evaluation algorithms, which are used to identify high-potential Fix and Flip opportunities.

We own the domain name "rapidflipinvestments.com" and associated social media handles to ensure consistent branding and prevent impersonation. We plan to further protect our unique renovation tracking system through patent filing within the next year, safeguarding our competitive edge.`,
    },

    {
      header: 'E-Commerce',
      content: `Our brand name, "EcoFriendly Essentials," is a registered trademark in the United States, ensuring exclusive rights to our branding across all product categories. We have also filed a design patent for our biodegradable packaging solutions, which include innovative reusable adhesives.

Our digital assets, including the domain "ecofriendlyessentials.com" and all associated social media accounts, are fully owned and protected. We continuously monitor for potential infringements to maintain the integrity of our brand.`,
    },

    {
      header: 'Book Publishing',
      content: `"Bright Pages Publishing" is a registered trademark in the United States, covering our business name and logo. Additionally, all books published under our brand are protected by copyright, ensuring exclusive rights for our authors and our company.

We own the domain name brightpagespublishing.com and maintain control over all digital distribution channels, including e-book and audiobook platforms. Moving forward, we plan to license certain titles for international markets, expanding our IP portfolio globally.`,
    },
  ],

  SERVICE_LINE__RESEARCH_AND_DEVELOPMENT: [
    {
      header: 'Real Estate Fix & Flip',
      content: `We are actively investing in R&D to refine our property evaluation and renovation processes:
  - Market Trend Analysis: Conduct quarterly reviews of emerging housing market trends to identify undervalued neighborhoods with high appreciation potential.
  - Technology Integration: Develop a custom analytics tool that leverages machine learning to predict property ROI more accurately and reduce acquisition risks.
  - Eco-Friendly Renovations: Research sustainable materials and energy-efficient upgrades that appeal to environmentally conscious buyers, differentiating our properties in the market.

These initiatives aim to improve profitability, reduce project timelines, and position us as leaders in innovative, sustainable real estate investment practices.`,
    },

    {
      header: 'E-Commerce',
      content: `Our R&D efforts focus on expanding our product line and improving customer experiences:
  - Material Innovation: Explore biodegradable and recycled materials for new product categories, such as reusable packaging and household goods.
  - Customer Feedback Integration: Implement a feedback system on our website and social media platforms to collect customer suggestions for new products and features.
  - Enhanced E-Commerce Features: Develop AI-driven product recommendation algorithms to personalize the shopping experience and increase conversion rates.

By prioritizing sustainable innovation and customer-centric improvements, we aim to enhance brand loyalty and meet the growing demand for eco-friendly products.`,
    },
    {
      header: 'Book Publishing',
      content: `We are actively investing in R&D to enhance our publishing processes and product offerings:
  - AI Writing Tools Optimization: Researching and implementing advanced AI models to improve the quality and efficiency of book drafting, enabling faster turnaround times for authors and publishers.
  - Enhanced Marketing Strategies: Developing proprietary algorithms for keyword optimization and Amazon PPC campaign analysis to maximize book visibility and sales.
  - Interactive Content Development: Exploring innovative formats such as interactive eBooks and activity-driven content like customizable crossword puzzles and coloring books to expand market reach.
These initiatives align with our goal to streamline publishing, diversify product offerings, and maintain a competitive edge in the rapidly evolving self-publishing industry.`,
    },
  ],

  MARKETING_AND_SALES__GROWTH_STRATEGY: [
    {
      header: 'Real Estate Fix & Flip',
      content: `To grow the company, we will do the following:
  - Expand property acquisition efforts to include high-growth suburban markets in addition to urban areas.
  - Establish partnerships with local contractors to streamline renovations and reduce project timelines.
  - Use targeted digital advertising to attract buyers interested in eco-friendly, energy-efficient homes.
  - Attend regional real estate investor conferences to build our network and identify potential collaborators.`,
    },

    {
      header: 'E-Commerce',
      content: `To grow the company, we will do the following:
  - Launch new product lines every quarter, focusing on emerging customer demands for eco-friendly alternatives.
  - Expand our online presence by collaborating with sustainable lifestyle influencers.
  - Open a flagship store in a high-traffic area to enhance brand visibility and provide an in-person shopping experience.
  - Implement an affiliate marketing program to drive online traffic and boost sales.`,
    },

    {
      header: 'Book Publishing',
      content: `To grow our publishing business, we will:
  - Expand our catalog by developing niche content, including activity books and specialized self-help titles, to attract diverse readerships.
  - Leverage Amazon PPC campaigns and social media advertising to boost visibility and drive sales across multiple book genres.
  - Build partnerships with influencers and book reviewers to enhance credibility and generate organic growth.
  - Explore new markets by translating popular titles into other languages and distributing through global eBook platforms.`,
    },
  ],

  MARKETING_AND_SALES__COMMUNICATE_WITH_THE_CUSTOMER: [
    {
      header: 'Real Estate Fix & Flip',
      content: `We will communicate with our customers by:
  - Providing regular email updates about available properties, renovation progress, and upcoming listings.
  - Using social media platforms, including Instagram and Facebook, to showcase before-and-after renovation photos and connect with potential buyers.
  - Offering one-on-one consultations to walk buyers through the benefits of purchasing renovated homes.
  - Collecting feedback from buyers through post-sale surveys to improve future projects and customer experiences.`,
    },

    {
      header: 'E-Commerce',
      content: `We will communicate with our customers by:
  - Sending a monthly email newsletter featuring new product launches, sustainability tips, and exclusive discounts.
  - Engaging with customers on social media platforms like Instagram, TikTok, and Twitter, sharing behind-the-scenes stories about product sourcing and production.
  - Implementing a live chat feature on our website for instant customer support.
  - Encouraging product reviews and testimonials on our website to build trust and improve offerings.`,
    },

    {
      header: 'Book Publishing',
      content: `We will communicate with our customers by:
  - Sending regular email newsletters with updates on new book releases, promotional discounts, and writing tips for aspiring authors.
  - Utilizing social media platforms like Instagram and Twitter to engage readers with interactive posts, behind-the-scenes content, and announcements.
  - Encouraging customer reviews and feedback on Amazon and Goodreads to build trust and improve our offerings.
  - Providing personalized responses to inquiries via email and chat support to ensure customer satisfaction and loyalty.`,
    },
  ],

  MARKETING_AND_SALES__HOW_TO_SELL: [
    {
      header: 'Real Estate Fix & Flip',
      content: `Our sales process is designed to maximize exposure for each property and attract serious buyers:
  - Initial Listing: Properties are professionally staged, photographed, and listed on high-traffic real estate platforms such as Zillow, Realtor.com, and MLS.
  - Open Houses and Showings: We host open houses and private showings to engage potential buyers and highlight the quality of our renovations.
  - Payment Process: Offers are submitted through our partner real estate agents, and all transactions are managed via escrow to ensure security.
  - Sales Support Team: Our in-house sales coordinator manages buyer inquiries, schedules showings, and ensures a seamless closing process.
  - As we grow, we plan to hire additional sales agents and implement CRM software to track leads and automate follow-ups.`,
    },

    {
      header: 'E-Commerce',
      content: `Our e-commerce platform makes the sales process fast and user-friendly:
  - Online Storefront: Customers browse and purchase products directly from our website, which features secure payment options such as credit cards, PayPal, and Apple Pay.
  - Social Media Shopping: Customers can shop directly from our Instagram and Facebook stores, with integrated checkout options.
  - Customer Support: A live chat feature is available to assist customers with questions or order issues.
  - Order Fulfillment: All orders are shipped within 24 hours using eco-friendly packaging.
  - As we grow, we plan to integrate AI-driven product recommendations and personalized email campaigns to increase sales.`,
    },

    {
      header: 'Book Publishing',
      content: `Our sales process is tailored to maximize book visibility and drive customer purchases:
  - Listing and Optimization: Books are uploaded to Amazon with optimized keywords, compelling descriptions, and professionally designed covers to attract potential buyers.
  - Promotions and Campaigns: We run Amazon PPC campaigns and seasonal promotions to boost sales and increase rankings.
  - Payment and Delivery: Customers purchase books directly through Amazon, ensuring secure payments and fast delivery through Amazon's fulfillment network.
  - Sales Support Tools: We use Amazon’s analytics tools to monitor performance and adjust strategies.
  - Customer Retention: Follow-up emails and exclusive discounts are sent to previous buyers to encourage repeat purchases and build a loyal reader base.`,
    },
  ],

  FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS: [
    {
      header: 'Real Estate Fix & Flip',
      content: `We are requesting $750,000 in funding over the next 12 months to scale our Fix & Flip operations in suburban markets. This funding will allow us to acquire, renovate, and sell 15 additional properties annually. The funding will be secured as a combination of a $500,000 line of credit and $250,000 from private investors. These funds will enable us to expand our portfolio, reduce project timelines, and increase profitability, with a projected ROI of 25% per project.`,
    },

    {
      header: 'Book Publishing',
      content: `We are seeking $150,000 in funding to scale our publishing operations over the next 12 months. The funding will primarily support content creation, marketing campaigns, design and production enhancements, and expansion into international markets. This investment will allow us to diversify our catalog, improve operational efficiency, and boost revenue by an estimated 30%. We are requesting this funding in the form of a business loan with favorable terms to support sustainable growth.`,
    },
  ],

  FUNDING_REQUEST__USE_OF_FUNDS: [
    {
      header: 'Real Estate Fix & Flip',
      content: `The $750,000 in funding will be allocated as follows:
  - Property Acquisition: $500,000 for purchasing undervalued properties in high-demand areas.
  - Renovation Costs: $200,000 for labor, materials, and contractor fees to complete high-quality renovations.
  - Marketing and Sales: $30,000 for professional staging, photography, and digital marketing to attract buyers.
  - Administrative Costs: $20,000 for legal fees, inspections, and operational expenses.`,
    },

    {
      header: 'Book Publishing',
      content: `The $150,000 in funding will be allocated as follows:
  - Content Creation: $50,000 for AI writing tools, ghostwriter services, and editorial support.
  - Marketing and Advertising: $40,000 for Amazon PPC campaigns, social media promotions, and influencer collaborations.
  - Design and Production: $30,000 for professional book cover designs, formatting, and print setup.
  - Expansion to International Markets: $20,000 for translating titles and global eBook platform distribution.
  - Operational Costs: $10,000 for administrative expenses and software tools.`,
    },
  ],

  FUNDING_REQUEST__SOURCE_OF_FUNDS: [
    {
      header: 'Real Estate Fix & Flip',
      content: `Our funding will come from a combination of personal investment, short-term financing, and private lenders:
  - Personal Investment: $150,000 from the owner’s savings to cover initial property acquisitions and renovations.
  - Private Lenders: $400,000 secured through relationships with real estate investors who specialize in short-term loans for Fix & Flip projects, with an average interest rate of 8%.
  - Line of Credit: $200,000 pre-approved from Local First Bank, specifically for property purchases in high-demand areas.

These sources will ensure we have the flexibility to complete 10-15 projects annually, with repayment structured around property sales.`,
    },

    {
      header: 'E-Commerce',
      content: `Our funding will be a combination of personal investment, a business loan, and equity financing:
  - Personal Investment: $50,000 contributed by the founders to cover initial inventory purchases and operational expenses.
  - Small Business Loan: $200,000 secured through the SBA, with a 6% fixed interest rate over five years, to finance website upgrades, marketing campaigns, and additional inventory.
  - Equity Investment: $250,000 from angel investors, in exchange for a 10% equity stake, to fund long-term growth initiatives, including international market expansion.

This diversified funding approach ensures stability and growth potential while maintaining a manageable debt load and strategic use of equity.`,
    },

    {
      header: 'Book Publishing',
      content: `Our funding will be sourced through a combination of personal contributions, small business financing, and publishing-focused investors:
  - Personal Investment: $25,000 from the owner’s savings to fund early-stage content creation and book production.
  - Small Business Loan: $50,000 secured through a government-backed loan program for small publishers, allocated for marketing campaigns and distribution.
  - Equity Financing: $75,000 from two private investors with expertise in the publishing industry, providing strategic guidance alongside funding.
  - This funding mix demonstrates a strong personal commitment while leveraging external resources to drive sustainable growth in our publishing business.`,
    },
  ],

  FUNDING_REQUEST__FINANCIAL_IMPACT_AND_BENEFITS: [
    {
      header: 'Real Estate Fix & Flip',
      content: `The requested $750,000 in funding will enable us to:
  - Increase Annual Projects: Expand our capacity to complete 15 additional Fix & Flip projects annually, generating an estimated $1.2 million in additional revenue.
  - Boost Profit Margins: Improve profit margins by 20% through bulk purchasing of materials and streamlined contractor agreements.
  - Reduce Turnaround Time: Cut average project timelines from six months to four months, allowing us to reinvest funds more quickly.

These improvements will yield an ROI of 25% per project and increase overall business profitability by 35% within the first year. For lenders, this ensures timely repayments, while equity investors can expect dividend distributions starting in year two.`,
    },

    {
      header: 'E-Commerce',
      content: `The $500,000 funding will drive significant growth across key areas of our e-commerce business:
  - Revenue Growth: Expand product offerings to increase monthly revenue from $100,000 to $250,000 within 18 months.
  - Customer Retention: Implement personalized shopping features that improve customer retention by 15%, leading to higher lifetime value.
  - Market Expansion: Launch targeted marketing campaigns to enter two new regional markets, capturing an additional 10% market share.

With these investments, we project a 3x return on funding within three years, making this a highly lucrative opportunity for both lenders and equity investors.`,
    },
    {
      header: 'Book Publishing',
      content: `The requested $150,000 in funding will enable us to:
  - Expand Revenue Streams: Diversify our book catalog to include niche genres and international editions, increasing annual revenue by an estimated 40%.
  - Boost Sales Performance: Enhance marketing efforts, resulting in a projected 25% increase in sales through improved visibility and customer engagement.
  - Improve Efficiency: Leverage AI tools and streamlined processes to reduce production timelines by 30%, enabling faster publishing cycles.
  - Strengthen Market Position: Expand our reach into international markets and solidify our brand as a leader in innovative and high-quality publishing.
These improvements will ensure sustainable growth, deliver attractive returns for investors, and position the business for long-term success.`,
    },
  ],

  FUNDING_REQUEST__REPAYMENT_PLAN_FOR_LOANS: [
    {
      header: 'Real Estate Fix & Flip',
      content: `We are requesting a $500,000 short-term loan with an annual interest rate of 8%, to be repaid within 12 months.
  - Repayment Schedule: The loan will be repaid in four quarterly installments of $130,000, covering both principal and interest.
  - Revenue Alignment: Each repayment will be made from the proceeds of completed property sales. With an average project ROI of 25%, we will generate sufficient cash flow to meet these payments.
  - Contingency Plan: We maintain a reserve fund of $50,000 to cover unexpected delays or market fluctuations, ensuring timely repayments.`,
    },

    {
      header: 'E-Commerce',
      content: `We are requesting a $200,000 small business loan at a fixed interest rate of 6% over five years.

  - Repayment Schedule: Monthly payments of $3,866, including both principal and interest.
  - Revenue Support: Our projected monthly revenue of $50,000 provides a debt-service coverage ratio of 2.5x, ensuring consistent repayments without straining cash flow.
  - Contingency Plan: We have allocated a portion of the funding to operational reserves, providing flexibility to maintain payments during seasonal revenue fluctuations.`,
    },

    {
      header: 'Book Publishing',
      content: `We are requesting a $100,000 loan with a 7% annual interest rate, to be repaid over 24 months.
  - Repayment Schedule: Monthly payments of $4,500, covering both principal and interest, will be made starting 60 days after loan disbursement.
  - Revenue Alignment: Repayments will be funded from increased sales revenue, projected to grow by 25% within the first year due to enhanced marketing and expanded product offerings.
  - Contingency Plan: A reserve fund of $15,000 is maintained to cover any unexpected delays in sales, ensuring consistent and timely repayments.
This structured plan ensures reliable debt management and lender confidence.`,
    },
  ],
};

export const chatGptContextDependencyMap: Partial<
  Record<BusinessPlanSurveySectionGuidanceKey, BusinessPlanSurveySectionGuidanceKey[]>
> = {
  EXECUTIVE_SUMMARY__FUTURE_OF_COMPANY: [
    'EXECUTIVE_SUMMARY__PRODUCT',
    'EXECUTIVE_SUMMARY__CUSTOMERS',
  ],
  COMPANY_DESCRIPTION__MISSION_STATEMENT: [
    'EXECUTIVE_SUMMARY__PRODUCT',
    'EXECUTIVE_SUMMARY__CUSTOMERS',
  ],
  MARKET_RESEARCH__INDUSTRY: ['EXECUTIVE_SUMMARY__PRODUCT', 'EXECUTIVE_SUMMARY__CUSTOMERS'],
  MARKET_RESEARCH__DETAILED_CUSTOMERS: [
    'EXECUTIVE_SUMMARY__PRODUCT',
    'EXECUTIVE_SUMMARY__CUSTOMERS',
  ],
  MARKET_RESEARCH__COMPANY_ADVANTAGES: [
    'MARKET_RESEARCH__DETAILED_CUSTOMERS',
    'EXECUTIVE_SUMMARY__PRODUCT',
  ],
  MARKET_RESEARCH__REGULATIONS: [
    'MARKET_RESEARCH__INDUSTRY',
    'MARKET_RESEARCH__COMPANY_ADVANTAGES',
    'EXECUTIVE_SUMMARY__PRODUCT',
  ],
  SERVICE_LINE__PRODUCT_SERVICE: [
    'EXECUTIVE_SUMMARY__PRODUCT',
    'EXECUTIVE_SUMMARY__CUSTOMERS',
    'MARKET_RESEARCH__COMPANY_ADVANTAGES',
  ],
  SERVICE_LINE__PRICING_STRUCTURE: [
    'EXECUTIVE_SUMMARY__PRODUCT',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'EXECUTIVE_SUMMARY__CUSTOMERS',
    'MARKET_RESEARCH__COMPANY_ADVANTAGES',
  ],
  SERVICE_LINE__PRODUCT_LIFECYCLE: [
    'EXECUTIVE_SUMMARY__CUSTOMERS',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'SERVICE_LINE__PRICING_STRUCTURE',
  ],
  SERVICE_LINE__INTELLECTUAL_PROPERTY_RIGHTS: [
    'EXECUTIVE_SUMMARY__PRODUCT',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'SERVICE_LINE__PRODUCT_LIFECYCLE',
  ],
  SERVICE_LINE__RESEARCH_AND_DEVELOPMENT: [
    'SERVICE_LINE__PRODUCT_SERVICE',
    'SERVICE_LINE__PRODUCT_LIFECYCLE',
  ],
  MARKETING_AND_SALES__GROWTH_STRATEGY: [
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKET_RESEARCH__DETAILED_CUSTOMERS',
    'SERVICE_LINE__PRODUCT_LIFECYCLE',
  ],
  MARKETING_AND_SALES__COMMUNICATE_WITH_THE_CUSTOMER: [
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKET_RESEARCH__DETAILED_CUSTOMERS',
    'MARKETING_AND_SALES__GROWTH_STRATEGY',
  ],
  MARKETING_AND_SALES__HOW_TO_SELL: [
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKET_RESEARCH__DETAILED_CUSTOMERS',
    'MARKETING_AND_SALES__COMMUNICATE_WITH_THE_CUSTOMER',
  ],
  FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS: [
    'EXECUTIVE_SUMMARY__FUTURE_OF_COMPANY',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKET_RESEARCH__DETAILED_CUSTOMERS',
    'MARKETING_AND_SALES__HOW_TO_SELL',
  ],
  FUNDING_REQUEST__USE_OF_FUNDS: [
    'FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKET_RESEARCH__DETAILED_CUSTOMERS',
    'MARKETING_AND_SALES__HOW_TO_SELL',
  ],
  FUNDING_REQUEST__SOURCE_OF_FUNDS: [
    'FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS',
    'FUNDING_REQUEST__USE_OF_FUNDS',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKETING_AND_SALES__HOW_TO_SELL',
  ],
  FUNDING_REQUEST__FINANCIAL_IMPACT_AND_BENEFITS: [
    'FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS',
    'FUNDING_REQUEST__USE_OF_FUNDS',
    'FUNDING_REQUEST__SOURCE_OF_FUNDS',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKETING_AND_SALES__HOW_TO_SELL',
  ],
  FUNDING_REQUEST__REPAYMENT_PLAN_FOR_LOANS: [
    'FUNDING_REQUEST__OVERVIEW_OF_FUNDING_NEEDS',
    'FUNDING_REQUEST__USE_OF_FUNDS',
    'FUNDING_REQUEST__SOURCE_OF_FUNDS',
    'SERVICE_LINE__PRODUCT_SERVICE',
    'MARKETING_AND_SALES__HOW_TO_SELL',
  ],
};

export type SurveySection = {
  key: BusinessPlanSurveySectionGuidanceKey;
  section: string;
  subsection: string;
  value: string;
};

export type SurveySectionGuidance = {
  key: BusinessPlanSurveySectionGuidanceKey;
  instruction: string;
  examples: string[];
  chatGptGenerateTemplatePrompt: string;
  chatGptGenerateTemplateSystemMessage: string;
};

export type BusinessPlanSurvey = {
  sections: SurveySection[];
  version: number | null;
};

export const SURVEY_ITEM_INTRO = 'introduction';
export const SURVEY_ITEM_VARIABLES = 'variables';
export const SURVEY_ITEM_SECTION = 'section';
export const SURVEY_ITEM_FINANCIAL_ANALYSIS = 'financial';
export const SURVEY_ITEM_REVIEW = 'review';

export type SurveyBaseItemViewModel = {
  type:
    | typeof SURVEY_ITEM_INTRO
    | typeof SURVEY_ITEM_VARIABLES
    | typeof SURVEY_ITEM_SECTION
    | typeof SURVEY_ITEM_FINANCIAL_ANALYSIS
    | typeof SURVEY_ITEM_REVIEW;
};

export type SurveyIntroViewModel = SurveyBaseItemViewModel & {
  type: typeof SURVEY_ITEM_INTRO;
};

export type SurveyVariablesViewModel = SurveyBaseItemViewModel & {
  type: typeof SURVEY_ITEM_VARIABLES;
};

export type SurveySectionViewModel = SurveyBaseItemViewModel &
  SurveySection & {
    type: typeof SURVEY_ITEM_SECTION;
    metadata: {
      instruction: string;
      examples: BusinessPlanSurveyExample[];
    };
  };

export type SurveyFinancialAnalysisViewModel = SurveyBaseItemViewModel & {
  type: typeof SURVEY_ITEM_FINANCIAL_ANALYSIS;
};

export type SurveyReviewViewModel = SurveyBaseItemViewModel & {
  type: typeof SURVEY_ITEM_REVIEW;
  version: number | null;
};

export type SurveyItemViewModel =
  | SurveyIntroViewModel
  | SurveyVariablesViewModel
  | SurveySectionViewModel
  | SurveyFinancialAnalysisViewModel
  | SurveyReviewViewModel;

export type SurveyViewModel = {
  sections: SurveySectionViewModel[];
  version: number | null;
};

export type BusinessPlanDownloadUrl = {
  url: string | null;
  expiration: string | null;
};

export type BusinessPlanFinancialTemplateDownloadUrl = {
  url: string | null;
  errorMessage: string | null;
};
